import { loadBuffer } from './resourceUtils';

export let fontsData: FontData[];
export let fontsByFamily: Map<string, FontData>;

interface FontSpec {
  family: string;
  filename: string;
  fallbacks: string[];
}

export const fontSpecs: FontSpec[] = [
  {
    family: 'Noto Sans',
    filename: 'NotoSans-VariableFont_wdth,wght.ttf',
    fallbacks: ['system-ui', 'Helvetica Neue', 'Arial', 'sans-serif']
  },
  {
    family: 'Noto Color Emoji',
    filename: 'NotoColorEmoji-Regular.ttf',
    fallbacks: [
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Noto Emoji',
      'Twemoji',
      'EmojiOne Color',
      'Segoe UI Symbol'
    ]
  },
  {
    family: 'Noto Emoji',
    filename: 'NotoEmoji-VariableFont_wght.ttf',
    fallbacks: [
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Twemoji',
      'EmojiOne Color',
      'Segoe UI Symbol'
    ]
  },
  {
    family: 'Noto Sans Arabic',
    filename: 'NotoSansArabic-VariableFont_wdth,wght.ttf',
    fallbacks: ['system-ui', 'Geeza Pro', 'Amiri', 'Arial', 'sans-serif']
  },
  {
    family: 'Noto Sans Hebrew',
    filename: 'NotoSansHebrew-VariableFont_wdth,wght.ttf',
    fallbacks: ['system-ui', 'Arial Hebrew', 'Lucida Grande', 'sans-serif']
  },
  {
    family: 'Noto Sans JP',
    filename: 'NotoSansJP-VariableFont_wght.ttf',
    fallbacks: [
      'system-ui',
      'Hiragino Sans',
      'Hiragino Kaku Gothic ProN',
      'Meiryo',
      'MS PGothic',
      'sans-serif'
    ]
  },
  {
    family: 'Noto Sans KR',
    filename: 'NotoSansKR-VariableFont_wght.ttf',
    fallbacks: [
      'system-ui',
      'Apple SD Gothic Neo',
      'Malgun Gothic',
      'Gulim',
      'sans-serif'
    ]
  },
  {
    family: 'Noto Sans SC',
    filename: 'NotoSansSC-VariableFont_wght.ttf',
    fallbacks: ['system-ui', 'PingFang SC', 'Microsoft YaHei', 'Heiti SC', 'sans-serif']
  },
  {
    family: 'Noto Sans TC',
    filename: 'NotoSansTC-VariableFont_wght.ttf',
    fallbacks: [
      'system-ui',
      'PingFang TC',
      'Microsoft JhengHei',
      'Heiti TC',
      'sans-serif'
    ]
  },
  {
    family: 'Noto Sans Thai',
    filename: 'NotoSansThai-VariableFont_wdth,wght.ttf',
    fallbacks: ['system-ui', 'Thonburi', 'Tahoma', 'sans-serif']
  }
];

export const fontFamilies: string[] = fontSpecs.map(spec => spec.family);

export class FontData {
  #family: string;
  #fallbacks: string[];
  #familyWithFallbacks: string;
  #filename: string;
  #buffer: ArrayBuffer;

  constructor(spec: FontSpec, buffer: ArrayBuffer) {
    this.#family = spec.family;
    this.#fallbacks = spec.fallbacks;
    this.#familyWithFallbacks = [spec.family, ...spec.fallbacks].join(', ');
    this.#filename = spec.filename;
    this.#buffer = buffer;
  }

  get family(): string {
    return this.#family;
  }

  get fallbacks(): string[] {
    return this.#fallbacks;
  }

  get familyWithFallbacks(): string {
    return this.#familyWithFallbacks;
  }

  get filename(): string {
    return this.#filename;
  }

  get buffer(): ArrayBuffer {
    return this.#buffer;
  }

  get placeholderUrl(): URL {
    return new URL(`file:///${this.#filename}`);
  }
}

export async function loadFontsData(
  prefix: string[],
  load?: typeof loadBuffer
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (fontsData !== undefined) {
    return;
  }
  if (!load) {
    load = loadBuffer;
  }
  const buffers = Promise.all(
    fontSpecs.map(async spec => {
      return load(prefix, spec.filename);
    })
  );
  fontsData = await buffers.then(buffers => {
    return buffers.map((buffer, index) => {
      const spec = fontSpecs[index];
      return new FontData(spec, buffer);
    });
  });
  fontsByFamily = new Map(fontsData.map(font => [font.family, font]));
}

export function destroyFontsData(): void {
  fontsData = [];
}
